const MAX_IMAGE_BYTES = 5_000_000
const MAX_IMAGE_DIMENSION = 2000
// Camera originals can be larger locally; only the prepared photo is uploaded.
const MAX_SOURCE_BYTES = 20 * 1024 * 1024

/**
 * Builds the source used by edit and clone photo previews.
 * @param picture A data URI or bare base64 from an older product response.
 * @returns A data URI, or an empty string when no picture exists.
 */
export function productImageSource(picture?: string | null): string {
  const normalizedPicture = picture?.trim()
  if (!normalizedPicture) return ''
  if (normalizedPicture.startsWith('data:image')) return normalizedPicture

  const type = normalizedPicture.startsWith('/9j/') ? 'jpeg' : 'png'
  return `data:image/${type};base64,${normalizedPicture}`
}

/**
 * Fits a selected photo or an existing data URI within the API upload limits.
 * Preserves its proportions and PNG transparency without enlarging small photos.
 * @param source A selected file or the data URI from an edit or clone form.
 * @returns The original photo if it fits, otherwise a resized JPEG or PNG.
 * @throws Error if the photo cannot be read or cannot meet the upload limits.
 */
export async function prepareProductImage(
  source: Blob | string,
): Promise<Blob> {
  const isFile = source instanceof Blob
  const type = isFile
    ? source.type
    : /^data:(image\/\w+);base64,/.exec(source)?.[1]
  if (!type || !['image/jpeg', 'image/jpg', 'image/png'].includes(type))
    throw new Error('format')
  if (isFile && source.size > MAX_SOURCE_BYTES) throw new Error('size')

  const image = new Image()
  const url = isFile ? URL.createObjectURL(source) : source
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('format'))
      image.src = url
    })

    const width = image.naturalWidth
    const height = image.naturalHeight
    if (!width || !height) throw new Error('format')
    const base64 = isFile ? '' : source.slice(source.indexOf(',') + 1)
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
    const sourceBytes = isFile ? source.size : (base64.length * 3) / 4 - padding
    // Avoid another round of compression when the photo already fits.
    if (
      Math.max(width, height) <= MAX_IMAGE_DIMENSION &&
      sourceBytes <= MAX_IMAGE_BYTES
    ) {
      if (isFile) return source
      const bytes = Uint8Array.from(atob(base64), (character) =>
        character.charCodeAt(0),
      )
      return new Blob([bytes], { type })
    }

    // Resize before upload so the API never has to decode the camera original.
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))
    const context = canvas.getContext('2d')
    if (!context) throw new Error('format')
    context.imageSmoothingQuality = 'high'
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error('format'))),
        type === 'image/png' ? 'image/png' : 'image/jpeg',
        0.85,
      )
    })
    if (blob.size > MAX_IMAGE_BYTES) throw new Error('size')
    return blob
  } finally {
    if (isFile) URL.revokeObjectURL(url)
  }
}

/**
 * Encodes a prepared photo for the product form's data URI field.
 * @param blob The photo returned by prepareProductImage.
 * @returns A data URI, rejecting if the browser cannot read the photo.
 */
export function productImageDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('format'))
    reader.onerror = () => reject(new Error('format'))
    reader.readAsDataURL(blob)
  })
}
